export const ROUTES = {
  root: {
    path: '/',
    create: () => '/',
  },
  auth: {
    alfaCallback: {
      path: '/auth/callback/alfa',
      create: () => '/auth/callback/alfa',
    },
    phoneLogin: {
      path: '/auth/phone-login',
      create: () => '/auth/phone-login',
    },
    totpSetup: {
      path: '/auth/totp-setup',
      create: () => '/auth/totp-setup',
    },
    passwordReset: {
      path: '/auth/password-reset',
      create: () => '/auth/password-reset',
    },
  },
  organization: {
    path: '/organization/:id',
    create: (id: string | number) => `/organization/${id}`,
    documents: {
      relativePath: 'documents',
      path: '/organization/:id/documents',
      create: (id: string) => `/organization/${id}/documents`,
      chat: {
        relativePath: 'chat',
        path: '/organization/:id/documents/:documentId?/chat',
        create: (id: string, documentId?: string) =>
          `/organization/${id}/documents${documentId ? `/${documentId}` : ''}/chat`,
      },
      document: {
        relativePath: ':documentId',
        path: '/organization/:id/documents/:documentId',
        create: (id: string, documentId: string) => `/organization/${id}/documents/${documentId}`,
        summary: {
          relativePath: 'summary',
          path: '/organization/:id/documents/:documentId/summary',
          create: (id: string, documentId: string) => `/organization/${id}/documents/${documentId}/summary`,
        },
        aiSummary: {
          relativePath: 'ai-summary',
          path: '/organization/:id/documents/:documentId/ai-summary',
          create: (id: string, documentId: string) => `/organization/${id}/documents/${documentId}/ai-summary`,
        },
        properties: {
          relativePath: 'properties',
          path: '/organization/:id/documents/:documentId/properties',
          create: (id: string, documentId: string) => `/organization/${id}/documents/${documentId}/properties`,
        },
        analyze: {
          relativePath: 'analyze',
          path: '/organization/:id/documents/:documentId/analyze',
          create: (id: string, documentId: string) => `/organization/${id}/documents/${documentId}/analyze`,
          start: {
            relativePath: ':typeId/start',
            path: '/organization/:id/documents/:documentId/analyze/:typeId/start',
            create: (id: string, documentId: string, typeId: string | number) =>
              `/organization/${id}/documents/${documentId}/analyze/${typeId}/start`,
          },
          result: {
            relativePath: ':typeId/result',
            path: '/organization/:id/documents/:documentId/analyze/:typeId/result',
            create: (id: string, documentId: string, typeId: string | number) =>
              `/organization/${id}/documents/${documentId}/analyze/${typeId}/result`,
          },
          chat: {
            relativePath: ':typeId/chat',
            path: '/organization/:id/documents/:documentId/analyze/:typeId/chat',
            create: (id: string, documentId: string, typeId: string | number) =>
              `/organization/${id}/documents/${documentId}/analyze/${typeId}/chat`,
          },
        },
        documentPreview: {
          relativePath: 'document-preview',
          path: '/organization/:id/documents/:documentId/document-preview/:page?',
          create: (id: string, documentId: string, page?: string) =>
            `/organization/${id}/documents/${documentId}/document-preview${page ? `/${page}` : ''}`,
        },
      },
    },
    news: {
      relativePath: 'news',
      path: '/organization/:id/news',
      create: (id: string) => `/organization/${id}/news`,
      article: {
        relativePath: ':newsId',
        path: '/organization/:id/news/:newsId',
        create: (id: string, newsId: string) => `/organization/${id}/news/${newsId}`,
      },
    },
    notebook: {
      relativePath: 'notebook',
      path: '/organization/:id/notebook',
      create: (id: string) => `/organization/${id}/notebook`,
      notes: {
        relativePath: 'notes',
        path: '/organization/:id/notebook/notes',
        create: (id: string) => `/organization/${id}/notebook/notes`,
        createNote: {
          relativePath: 'create',
          path: '/organization/:id/notebook/notes/create',
          create: (id: string) => `/organization/${id}/notebook/notes/create`,
        },
        note: {
          relativePath: ':noteId',
          path: '/organization/:id/notebook/notes/:noteId',
          create: (id: string, noteId: string) => `/organization/${id}/notebook/notes/${noteId}`,
          edit: {
            relativePath: 'edit',
            path: '/organization/:id/notebook/notes/:noteId/edit',
            create: (id: string, noteId: string) => `/organization/${id}/notebook/notes/${noteId}/edit`,
          },
        },
      },
      followUps: {
        relativePath: 'follow-ups',
        path: '/organization/:id/notebook/follow-ups',
        create: (id: string) => `/organization/${id}/notebook/follow-ups`,
        createFollowUp: {
          relativePath: 'create',
          path: '/organization/:id/notebook/follow-ups/create',
          create: (id: string) => `/organization/${id}/notebook/follow-ups/create`,
        },
        followUp: {
          relativePath: ':followUpId',
          path: '/organization/:id/notebook/follow-ups/:followUpId',
          create: (id: string, followUpId: string) => `/organization/${id}/notebook/follow-ups/${followUpId}`,
          edit: {
            relativePath: 'edit',
            path: '/organization/:id/notebook/follow-ups/:followUpId/edit',
            create: (id: string, followUpId: string) => `/organization/${id}/notebook/follow-ups/${followUpId}/edit`,
          },
        },
      },
      insights: {
        relativePath: 'insights',
        path: '/organization/:id/notebook/insights',
        create: (id: string) => `/organization/${id}/notebook/insights`,
        createInsight: {
          relativePath: 'create',
          path: '/organization/:id/notebook/insights/create',
          create: (id: string) => `/organization/${id}/notebook/insights/create`,
        },
        insight: {
          relativePath: ':insightId',
          path: '/organization/:id/notebook/insights/:insightId',
          create: (id: string, insightId: string) => `/organization/${id}/notebook/insights/${insightId}`,
          edit: {
            relativePath: 'edit',
            path: '/organization/:id/notebook/insights/:insightId/edit',
            create: (id: string, insightId: string) => `/organization/${id}/notebook/insights/${insightId}/edit`,
          },
        },
      },
    },
    macro: {
      relativePath: 'macro',
      path: '/organization/:id/macro',
      create: (id: string) => `/organization/${id}/macro`,
      analyze: {
        relativePath: 'analyze',
        path: '/organization/:id/macro/analyze',
        create: (id: string) => `/organization/${id}/macro/analyze`,
        result: {
          relativePath: ':typeId/result',
          path: '/organization/:id/macro/analyze/:typeId/result',
          create: (id: string, typeId: string | number) => `/organization/${id}/macro/analyze/${typeId}/result`,
        },
        chat: {
          relativePath: ':typeId/chat',
          path: '/organization/:id/macro/analyze/:typeId/chat',
          create: (id: string, typeId: string | number) => `/organization/${id}/macro/analyze/${typeId}/chat`,
        },
      },
    },
  },
} as const;
